"use client"

import { useState, useRef, useEffect, useMemo } from "react"
import Modal from "../modules/Modal/Moadal"
import Button from "../modules/Button/Button"
import TextArea from "../modules/TextArea/TextArea"
import Select from "../modules/Select/Select"
import styles from "../../../styles/inventory/addRequestModal.module.css"

import { searchDevicesByImei, createWarehouseTicket } from "../../../services/inventory/devices"

const AddRequestModal = ({ isOpen, onClose, onSubmit, tenant: tenantProp = null }) => {
  const [selectedDeviceImei, setSelectedDeviceImei] = useState("")
  const [issueDescription, setIssueDescription] = useState("")

  const selectedDeviceObjectRef = useRef(null)
  const [selectedDeviceObject, setSelectedDeviceObject] = useState(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const resolveTenant = () => {
    if (tenantProp) return tenantProp
    try {
      const acc = localStorage.getItem("inventory_active_account")
      if (!acc) return null
      const parsed = JSON.parse(acc)
      return parsed?.id ?? null
    } catch {
      return null
    }
  }
  const tenant = resolveTenant()

  useEffect(() => {
    selectedDeviceObjectRef.current = selectedDeviceObject
  }, [selectedDeviceObject])

  const mapDeviceToOption = (device) => {
    const imei = device.serial ?? device.imei ?? device.serial_no ?? "unknown"
    const labelName = device.name ?? device.device_type?.name ?? device.device_type_name ?? "Unknown device"
    const typeName = (device.device_type && device.device_type.name) || device.device_type_name || "Unknown Type"
    return {
      value: imei,
      label: `${imei} (${labelName})`,
      name: labelName,
      type: typeName,
      id: device.id ?? null,
      original: device,
    }
  }

  /**
   * imeiAsyncFetch(searchQuery, pageOrUrl)
   *
   * - If searchQuery is empty -> return empty results (no API call).
   * - If pageOrUrl is a string starting with "http" -> treat it as a full URL and call searchDevicesByImei(URL, q)
   *   so the backend full link is requested (this ensures inventory/warehouse/devices/?page=2&q=2 is hit).
   * - Otherwise call searchDevicesByImei(tenantOrDefault, q, pageNumber)
   *
   * Expected return shape: { results: [ ...mapped options... ], meta: {...} }
   */
  const imeiAsyncFetch = async (q, pageOrUrl = 1) => {
    const query = q !== undefined && q !== null ? String(q).trim() : ""
    if (!query) {
      return { results: [], meta: null }
    }

    try {
      let res

      // If pageOrUrl is a full URL (string) — call that URL directly so backend URL is hit.
      if (typeof pageOrUrl === "string" && pageOrUrl.startsWith("http")) {
        // call searchDevicesByImei with the URL as the first arg (tenantOrUrl)
        res = await searchDevicesByImei(pageOrUrl, query)
      } else {
        // otherwise call with tenant (or default warehouse endpoint) and page number
        const tenantOrDefault = tenant || "/inventory/warehouse/devices/"
        res = await searchDevicesByImei(tenantOrDefault, query, pageOrUrl)
      }

      // Normalize results/meta
      const results =
        (res && res.results) ||
        (res && res.data && res.data.results) ||
        (Array.isArray(res) ? res : []) ||
        []
      const meta = res?.meta ?? res?.data?.meta ?? null

      return {
        results: Array.isArray(results) ? results.map(mapDeviceToOption) : [],
        meta,
      }
    } catch (err) {
      console.error("IMEI search error:", err)
      return { results: [], meta: null }
    }
  }

  const handleSelectChange = (valOrObj, optionObj = null) => {
    // Select can return either the value or the full option object depending on prop.
    if (optionObj && typeof optionObj === "object") {
      setSelectedDeviceImei(optionObj.value)
      setSelectedDeviceObject(optionObj)
      selectedDeviceObjectRef.current = optionObj
    } else if (valOrObj && typeof valOrObj === "object") {
      setSelectedDeviceImei(valOrObj.value)
      setSelectedDeviceObject(valOrObj)
      selectedDeviceObjectRef.current = valOrObj
    } else {
      setSelectedDeviceImei(valOrObj)
      setSelectedDeviceObject(null)
      selectedDeviceObjectRef.current = null
    }
  }

  const handleSubmit = async () => {
    setError(null)
    if (!selectedDeviceImei || !issueDescription.trim()) {
      setError("Please select a device and enter an issue description.")
      return
    }

    setLoading(true)
    try {
      const payload = {
        device_id: selectedDeviceImei,
        issue_description: issueDescription.trim(),
      }

      const res = await createWarehouseTicket(payload)

      if (typeof onSubmit === "function") {
        try {
          onSubmit(res)
        } catch (err) {
          console.warn("onSubmit callback error:", err)
        }
      }

      handleReset()
      onClose()
    } catch (err) {
      console.error("Error creating warehouse ticket:", err)

      const serverMsg = err?.response?.data?.message || err?.message || "Failed to create ticket"
      setError(serverMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setSelectedDeviceImei("")
    setSelectedDeviceObject(null)
    selectedDeviceObjectRef.current = null
    setIssueDescription("")
    setError(null)
  }

  const renderSelectOption = (option) => (
    <div className={styles.dropdown_option_content}>
      <div className={styles.option_imei}>{option.value}</div>
      <div className={styles.option_type}>{option.type}</div>
    </div>
  )

  const modalFooter = (
    <div className={styles.modal_footer}>
      <Button variant="secondary" size="small" onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button variant="secondary" size="small" onClick={handleReset} disabled={loading}>
        Reset
      </Button>
      <Button variant="primary" size="small" onClick={handleSubmit} disabled={!selectedDeviceImei || !issueDescription.trim() || loading} loading={loading}>
        {loading ? "Creating..." : "Create"}
      </Button>
    </div>
  )

  return (
    <Modal title="Support Request" isOpen={isOpen} onClose={onClose} width="800px" height="auto" footer={modalFooter}>
      <div className={styles.modal_content}>
        <div className={styles.device_selection_section}>
          <div className={styles.device_dropdowns}>
            <div className={styles.dropdown_group}>
              <label className={styles.dropdown_label}>Select Device by IMEI</label>
              <Select
                value={selectedDeviceImei}
                onChange={(val, optionObj) => handleSelectChange(val, optionObj)}
                placeholder="Search by IMEI or device type..."
                searchable={true}
                width="100%"
                renderOption={renderSelectOption}
                asyncFetch={imeiAsyncFetch}
                debounceDelay={500}
                minSearchLength={1}
                returnFullOptionOnSelect={true}
                pagination={true} // enable pagination footer in dropdown
              />
            </div>
          </div>
        </div>

        {selectedDeviceObject && (
          <div className={styles.selected_devices_section}>
            <h4 className={styles.section_subtitle}>Selected Device Details</h4>
            <div className={styles.device_details_list}>
              <div className={styles.device_detail_card}>
                <div className={styles.device_detail_row}>
                  <span className={styles.device_detail_label}>Device Name:</span>
                  <span className={styles.device_detail_value}>{selectedDeviceObject.name}</span>
                </div>
                <div className={styles.device_detail_row}>
                  <span className={styles.device_detail_label}>Serial No:</span>
                  <span className={styles.device_detail_value}>{selectedDeviceObject.value}</span>
                </div>
                <div className={styles.device_detail_row}>
                  <span className={styles.device_detail_label}>Type:</span>
                  <span className={styles.device_detail_value}>{selectedDeviceObject.type}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={styles.issue_section}>
          <TextArea
            label="Issue Description"
            placeholder="Describe the issue in detail..."
            value={issueDescription}
            onChange={(e) => setIssueDescription(e.target.value)}
            rows={4}
          />
        </div>

        {error && <div style={{ color: "var(--danger)", marginTop: 12 }}>{error}</div>}
      </div>
    </Modal>
  )
}

export default AddRequestModal
